import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import Paywall from '../components/Paywall';
import { isPurchasesAvailable } from '../lib/purchases';
import { invalidate } from '../api/apiCache';

// One paywall for the whole app, opened from anywhere via `usePaywall().openPaywall()`.
//
// A context rather than prop-drilling (which is how Grocerly does it) because Agence has
// five surfaces that need to trigger it — the upgrade nudge on Insights/Automations/
// Settings, the Pricing tiers, and the chat message limit — and threading a callback
// through all of them would touch far more code than it is worth.
//
// Every server-side plan gate already answers with `upgradeUrl: '/pricing'`
// (chat.js, insights.js, automations.js ×7), so `/pricing` remains the one place all
// upgrade paths converge; this just makes that destination purchase in-app on native.
const PaywallContext = createContext(null);

// Returned when there is no provider above. Deliberately inert rather than throwing:
// UpgradeNudge and Pricing both render on public marketing routes that sit outside the
// authenticated shell, and throwing there would white-screen a logged-out visitor over a
// purchase affordance they cannot use anyway.
const INERT = { openPaywall: () => {}, canPurchaseInApp: false };

export function usePaywall() {
  return useContext(PaywallContext) ?? INERT;
}

export function PaywallProvider({ children }) {
  const [open, setOpen] = useState(false);

  // Read at call time, not module load: it depends on the platform and on env values, and
  // reading it per render keeps tests honest about which build they are simulating.
  const canPurchaseInApp = isPurchasesAvailable();

  const openPaywall = useCallback(() => {
    // Never open a paywall that cannot load an offering. On a misconfigured build (missing
    // or `test_` key) the modal would show "Couldn't load subscription options" with no way
    // to proceed — worse than the caller simply not offering the action.
    if (!isPurchasesAvailable()) return;
    setOpen(true);
  }, []);

  const closePaywall = useCallback(() => setOpen(false), []);

  const handlePurchased = useCallback(() => {
    // The RevenueCat webhook updates `users.iap_plan` server-side, but the client caches
    // the profile in sessionStorage — without this, the plan the user just paid for stays
    // stale and the feature they bought remains locked until a reload.
    invalidate('profile');
    // Same signal the chat tools use after a mutation, so any mounted screen refetches.
    window.dispatchEvent(new CustomEvent('agence:data-changed', { detail: { keys: ['profile'] } }));
    setOpen(false);
  }, []);

  const value = useMemo(
    () => ({ openPaywall, canPurchaseInApp }),
    [openPaywall, canPurchaseInApp]
  );

  return (
    <PaywallContext.Provider value={value}>
      {children}
      {open && <Paywall onClose={closePaywall} onPurchased={handlePurchased} />}
    </PaywallContext.Provider>
  );
}
